import names from '../names'
import '../index.scss'
import {useEffect, useRef, useState} from 'react'
import {HelpIcon, InvalidIcon, LockIcon, ValidIcon} from '../../../icons'
import InputInfo from '../../../components/core/inputInfo'
import {Formik} from 'formik'
import {getSslIconColor, getSslTooltipText} from '../../../utils/ssl'
import useOnClickOutside from '../../../utils/hooks/useOutsideClick'
import {domainFormRef} from '../refs'
import Switch from '../../../components/core/switch'
import LimitManager from '../../../utils/limitManager'
import Tooltip from '../../../components/core/tooltip'
import Input from '../../../components/core/input'
import {useDispatch, useSelector} from 'react-redux'
import {getDomainCname} from '../../../stores/statuspage'
import {statuspageDomainSchema} from '../../../utils/validation'
import {colors} from '../../../utils/colors'
import renderSkeletonLoaders from '../../../utils/loader'
import classNames from 'classnames'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../../utils/env'
import {DateFormats, formatDate} from '../../../utils/date'

const DomainForm = ({
    id,
    redirectToHttps,
    setRedirectToHttps,
    statuspageData,
    isLimitExceeded,
    isDarkMode
}) => {
    const text = useNamespaceTranslations('statuspages')
    const usageLimitsData = useNamespaceTranslations('usage-limits')
    const validationText = useNamespaceTranslations('validation')
    const [domain, setDomain] = useState('')
    const redirectToHttpsInfoRef = useRef()
    const [active, setActive] = useState(null)

    const dispatch = useDispatch()

    const cnameData = useSelector((state) => state.statuspage.cnameData)
    const isProcessing = useSelector((state) => state.statuspage.isProcessing)

    useEffect(() => {
        if (id) {
            setDomain(statuspageData?.common?.domain)
            setRedirectToHttps(statuspageData?.common?.redirect_to_https)
            statuspageData?.common?.domain &&
         dispatch(getDomainCname(statuspageData?.common?.domain))
        }
    }, [JSON.stringify(statuspageData)])

    useOnClickOutside(redirectToHttpsInfoRef, () =>
        setActive(active === names.redirect_to_https && '')
    )

    const initialFormValues = {
        domain: id ? statuspageData?.common?.domain || '' : ''
    }

    const getCnameStatusIcon = () => {
        if (cnameData?.configured) {
            return <ValidIcon width={20} height={20} />
        }
        if (cnameData?.value) {
            return <InvalidIcon width={20} height={18} />
        }
        return <InvalidIcon width={20} height={18} stroke={colors.colorOrange} />
    }

    const getCnameStatusText = () => {
        if (cnameData?.configured) {
            return text.valid
        }
        if (cnameData?.value) {
            return (
                <span className="invalid">
                    {text.invalid}
                    <span className="text">
                        {` (${text.current}: ${cnameData?.value})`}
                    </span>
                </span>
            )
        }
        return text.not_found
    }

    const handleInfo = (index, state) => {
        state ? setActive(index) : setActive(null)
    }

    useEffect(() => {
        const formik = domainFormRef.current
        if (!formik) return

        if (!formik.errors || Object.keys(formik.errors).length === 0) return

        (async() => {
            const schemaErrors = await formik.validateForm(formik.values)
            const current = formik.errors || {}

            const serverOnly = {}
            for (const k of Object.keys(current)) {
                if (!schemaErrors || !(k in schemaErrors)) {
                    serverOnly[k] = current[k]
                }
            }

            formik.setErrors({...(schemaErrors || {}), ...serverOnly})
        })()
    }, [validationText])

    return (
        <div className="content domain">
            <Formik
                innerRef={domainFormRef}
                initialValues={initialFormValues}
                validateOnBlur={false}
                validateOnChange={false}
                enableReinitialize={true}
                validationSchema={statuspageDomainSchema}
                onSubmit
            >
                {({values, errors, handleChange, handleSubmit}) => (
                    <form onSubmit={handleSubmit} className="form-elements">
                        {isProcessing  ? renderSkeletonLoaders(1) :
                            !LimitManager.getStatusPageDomainEnabled(usageLimitsData) ? (
                                <Tooltip
                                    trigger="mouseenter"
                                    placement="top"
                                    content={text.premium_feature}
                                >
                                    <Input
                                        name={names.domain}
                                        value={values.domain}
                                        error={errors.domain}
                                        placeholder={text.domain_placeholder}
                                        infoText={text.domain_info}
                                        onChange={(e) => {
                                            handleChange(e)
                                            setDomain(e?.target?.value)
                                        }}
                                        activeInfo={active === names.domain}
                                        handleInfo={(state) => handleInfo(names.domain, state)}
                                        inputIsDisabled={(isLimitExceeded && !id) || !LimitManager.getStatusPageDomainEnabled(usageLimitsData)}
                                    />
                                </Tooltip>
                            ) : (
                                <Input
                                    name={names.domain}
                                    value={values.domain}
                                    error={errors.domain}
                                    placeholder={text.domain_placeholder}
                                    infoText={text.domain_info}
                                    onChange={(e) => {
                                        handleChange(e)
                                        setDomain(e?.target?.value)
                                        e?.target?.value && dispatch(getDomainCname(e?.target?.value))
                                    }}
                                    activeInfo={active === names.domain}
                                    handleInfo={(state) => handleInfo(names.domain, state)}
                                    inputIsDisabled={isLimitExceeded && !id}
                                />
                            )}
                        {isProcessing ? renderSkeletonLoaders(1, 'switch-skeleton') :
                            <>
                                <div className={classNames('subscription', {
                                    dark: isDarkMode
                                })}>
                                    {!LimitManager.getStatusPageDomainEnabled(usageLimitsData) ?
                                        <Tooltip
                                            trigger="mouseenter"
                                            placement="top"
                                            content={text.premium_feature}
                                        >
                                            <Switch
                                                name={names.redirect_to_https}
                                                checked={!redirectToHttps}
                                                onChange={() => setRedirectToHttps(!redirectToHttps)}
                                                isDisabled={(isLimitExceeded && !id) || !LimitManager.getStatusPageDomainEnabled(usageLimitsData)}
                                            />
                                        </Tooltip> :
                                        <Switch
                                            name={names.redirect_to_https}
                                            checked={redirectToHttps}
                                            onChange={() => setRedirectToHttps(!redirectToHttps)}
                                            isDisabled={isLimitExceeded && !id}
                                        />}
                                    <p>{text.redirect_to_https}</p>
                                    <div
                                        ref={redirectToHttpsInfoRef}
                                        className={`help ${
                                            active === names.redirect_to_https ? 'active' : ''
                                        }`}
                                        onClick={() =>
                                            setActive(
                                                active === names.redirect_to_https
                                                    ? ''
                                                    : names.redirect_to_https
                                            )
                                        }
                                    >
                                        <span>
                                            <HelpIcon />
                                        </span>
                                        <InputInfo text={text.redirect_to_https_info} />
                                    </div>
                                </div>
                                <div className={classNames('cname-container', {
                                    dark: isDarkMode
                                })}>
                                    {id && domain && (
                                        <div className="status">
                                            <div>
                                                <h6 className="text">{text.cname_is}</h6>
                                                <p className="desc">
                                                    {getCnameStatusIcon()}
                                                    {getCnameStatusText()}
                                                </p>
                                            </div>
                                            <div>
                                                <h6 className="text">{text.ssl_cert}</h6>
                                                <p className="desc">
                                                    <LockIcon
                                                        width={14}
                                                        height={18}
                                                        fill={getSslIconColor(
                                                            statuspageData?.common?.domain,
                                                            statuspageData?.common?.ssl_cert
                                                        )}
                                                    />
                                                    {getSslTooltipText(
                                                        statuspageData?.common?.domain,
                                                        statuspageData?.common?.ssl_cert
                                                    )}
                                                    {statuspageData?.common?.ssl_cert?.expires_at &&
                        !statuspageData?.common?.ssl_cert?.valid && (
                                                        <span className="expires">
                                                            {`(${text.expired}: ${formatDate(
                                                                statuspageData?.common?.ssl_cert?.expires_at,
                                                                DateFormats.DOT_DMY
                                                            )})`}
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <div className="cname-box">
                                        <p className="info">{text.cname_info}</p>
                                        <p className="info ssl-info">{text.custom_domain_ssl}</p>
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>{text.record_type}</th>
                                                    <th>{text.host}</th>
                                                    <th>{text.target}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>{text.cname}</td>
                                                    <td>{domain || text.example_custom_domain}</td>
                                                    <td>{envConfig.statuspageCnameValue}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>}
                    </form>
                )}
            </Formik>
        </div>
    )
}
export default DomainForm
